import type { PublicBookingSiteTheme } from '@tebuto/api/public/models/PublicBookingSiteTheme'
import { publicThemeBrandTokenCss } from '@tebuto/lib/public-theme-css'
import type { CSSProperties, ReactElement, ReactNode } from 'react'

export type SeminarPageColors = {
    primaryColor: string
    backgroundColor: string
    textPrimary: string
    textSecondary: string
    borderColor: string
}

export const DEFAULT_SEMINAR_PAGE_COLORS: SeminarPageColors = {
    primaryColor: '#0d9488',
    backgroundColor: '#ffffff',
    textPrimary: '#111827',
    textSecondary: '#4b5563',
    borderColor: '#e5e7eb'
}

const CSS_HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

function resolveColor(color: string | null | undefined, fallback: string): string {
    if (!color || !CSS_HEX_COLOR_PATTERN.test(color)) {
        return fallback
    }
    return color
}

export function resolveSeminarPageColors(theme: PublicBookingSiteTheme | null | undefined): SeminarPageColors {
    return {
        primaryColor: resolveColor(theme?.primaryColor, DEFAULT_SEMINAR_PAGE_COLORS.primaryColor),
        backgroundColor: resolveColor(theme?.backgroundColor, DEFAULT_SEMINAR_PAGE_COLORS.backgroundColor),
        textPrimary: resolveColor(theme?.textPrimary, DEFAULT_SEMINAR_PAGE_COLORS.textPrimary),
        textSecondary: resolveColor(theme?.textSecondary, DEFAULT_SEMINAR_PAGE_COLORS.textSecondary),
        borderColor: resolveColor(theme?.borderColor, DEFAULT_SEMINAR_PAGE_COLORS.borderColor)
    }
}

export function SeminarPageColorVariables({ colors }: { colors: SeminarPageColors }): ReactElement {
    const brandTokens = publicThemeBrandTokenCss(colors.primaryColor)
    return (
        <style>
            {`:root {
--seminar-page-primary-color: ${colors.primaryColor};
--seminar-page-background-color: ${colors.backgroundColor};
--seminar-page-text-primary: ${colors.textPrimary};
--seminar-page-text-secondary: ${colors.textSecondary};
--seminar-page-border-color: ${colors.borderColor};
--booking-site-primary-color: ${colors.primaryColor};
--booking-site-background-color: ${colors.backgroundColor};
--booking-site-text-primary: ${colors.textPrimary};
--booking-site-text-secondary: ${colors.textSecondary};
--booking-site-border-color: ${colors.borderColor};
${brandTokens}
}`}
        </style>
    )
}

export const SEMINAR_PAGE_MAX_WIDTH_CLASS = 'mx-auto w-full max-w-6xl'
export const SEMINAR_PAGE_FRAME_CLASS = `${SEMINAR_PAGE_MAX_WIDTH_CLASS} px-6 md:px-10 lg:px-16`

export function SeminarStickyBrandHeader({
    therapistName,
    therapistImage,
    logoUrl,
    colors,
    action,
    sticky = true
}: {
    therapistName: string
    therapistImage: string | null
    logoUrl: string | null
    colors: SeminarPageColors
    action?: ReactNode
    sticky?: boolean
}): ReactElement {
    return (
        <header
            className={`${sticky ? 'sticky top-0 z-20 ' : ''}border-b bg-white`}
            style={{ borderColor: colors.borderColor }}
        >
            <div className={`${SEMINAR_PAGE_FRAME_CLASS} flex items-center gap-4 py-3.5`}>
                {logoUrl ? (
                    <img src={logoUrl} alt="" className="h-8 max-w-40 shrink-0 object-contain" />
                ) : therapistImage ? (
                    <img
                        src={therapistImage}
                        alt={therapistName}
                        className="h-9 w-9 shrink-0 rounded-full object-cover"
                    />
                ) : null}
                <span className="truncate text-sm font-semibold" style={{ color: colors.textPrimary }}>
                    {therapistName}
                </span>
                <div className="ml-auto shrink-0">{action}</div>
            </div>
        </header>
    )
}

export function SeminarPageHeader({
    therapistName,
    therapistImage,
    logoUrl,
    title,
    subtitle,
    colors
}: {
    therapistName: string
    therapistImage: string | null
    logoUrl: string | null
    title: string
    subtitle?: string | null
    colors: SeminarPageColors
}): ReactElement {
    return (
        <header className="border-b" style={{ borderColor: colors.borderColor }}>
            <div className={`${SEMINAR_PAGE_FRAME_CLASS} flex items-center gap-4 py-5`}>
                {logoUrl ? (
                    <img src={logoUrl} alt="" className="h-10 max-w-[180px] shrink-0 object-contain" />
                ) : therapistImage ? (
                    <img
                        src={therapistImage}
                        alt={therapistName}
                        className="h-12 w-12 shrink-0 rounded-full object-cover"
                    />
                ) : null}
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium" style={{ color: colors.textSecondary }}>
                        {therapistName}
                    </p>
                    <h1
                        className="truncate text-xl font-semibold tracking-tight sm:text-2xl"
                        style={{ color: colors.textPrimary }}
                    >
                        {title}
                    </h1>
                    {subtitle ? (
                        <p className="mt-0.5 truncate text-sm" style={{ color: colors.textSecondary }}>
                            {subtitle}
                        </p>
                    ) : null}
                </div>
            </div>
        </header>
    )
}

export function SeminarPageShell({
    colors,
    children
}: {
    colors: SeminarPageColors
    children: ReactNode
}): ReactElement {
    const style = {
        backgroundColor: colors.backgroundColor,
        color: colors.textPrimary
    } as CSSProperties

    return (
        <div className="flex min-h-full flex-col" style={style}>
            {children}
        </div>
    )
}

/** Light tint of a themed color for chip/highlight backgrounds — same technique as `color-mix()` in the design mockup. */
export function tintColor(color: string, percent = 8): string {
    return `color-mix(in srgb, ${color} ${percent}%, #fff)`
}

export function formatEuroPrice(price: string): string {
    const parsed = Number(price)
    if (!Number.isFinite(parsed)) {
        return price
    }
    if (parsed === 0) {
        return 'Kostenlos'
    }
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(parsed)
}

export function formatSessionRange(start: string, end: string): string {
    const startDate = new Date(start)
    const endDate = new Date(end)
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return ''
    }

    const dateFormatter = new Intl.DateTimeFormat('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    })
    const timeFormatter = new Intl.DateTimeFormat('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
    })

    return `${dateFormatter.format(startDate)}, ${timeFormatter.format(startDate)}–${timeFormatter.format(endDate)} Uhr`
}

export function locationLabel(
    locationType: 'virtual' | 'onsite' | 'not-fixed',
    locationName: string | null,
    cityName: string | null
): string {
    if (locationType === 'virtual') {
        return locationName ?? 'Online'
    }
    if (locationName && cityName) {
        return `${locationName}, ${cityName}`
    }
    return locationName ?? cityName ?? 'Ort folgt'
}

/** Compact location for hero chips and run subtitles — city only when onsite. */
export function seminarLocationShortLabel(
    locationType: 'virtual' | 'onsite' | 'not-fixed',
    locationName: string | null,
    cityName: string | null
): string {
    if (locationType === 'onsite') {
        return cityName?.trim() || 'Ort folgt'
    }
    return locationLabel(locationType, locationName, cityName)
}

export function stripHtml(value: string): string {
    return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}
